import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getMyApplications } from "../../api/jobs";
import api from "../../api/client"; // Import API client
import StatusBadge from "../../components/ui/ui/StatusBadge";
import { Button } from "../../components/ui/button";
import ConfirmDialog from "../../components/common/ConfirmDialog"; // Import ConfirmDialog
import AnimatedBackground from "../../components/ui/AnimatedBackground";
import {
 Loader2,
 MapPin,
 DollarSign,
 Calendar,
 Building2,
 Eye,
 Briefcase,
 Trash2, // Import Trash icon
} from "lucide-react";

const MyApplications = () => {
 const [apps, setApps] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState("");
 const [actionLoading, setActionLoading] = useState({}); // Track action loading

 useEffect(() => {
 const fetchApplications = async () => {
 try {
 const res = await getMyApplications();
 const validApps = (res.data || []).filter(app => app && app.job);
 setApps(validApps);
 } catch (err) {
 console.error("Failed to fetch applications:", err);
 setError("Failed to load applications. Please try again.");
 } finally {
 setLoading(false);
 }
 };
 fetchApplications();
 }, []);

 const handleWithdraw = async (appId) => {
 setActionLoading((prev) => ({ ...prev, [appId]: true }));
 try {
 await api.delete(`/applier/applications/${appId}`);
 setApps((prev) => prev.filter((a) => a.id !== appId));
 } catch (err) {
 console.error(err);
 setError(err.response?.data?.message || "Failed to withdraw application.");
 } finally {
 setActionLoading((prev) => ({ ...prev, [appId]: false }));
 }
 };

 if (loading) {
 return (
 <div className="min-h-screen from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 relative overflow-hidden flex items-center justify-center">
 <AnimatedBackground />
 <Loader2 className="w-12 h-12 animate-spin text-indigo-600 dark:text-indigo-400 relative z-10" />
 </div>
 );
 }

 if (error) {
 return (
 <div className="min-h-screen from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 relative overflow-hidden flex items-center justify-center">
 <AnimatedBackground />
 <div className="text-center relative z-10">
 <p className="text-red-600 dark:text-red-400 text-lg mb-4">{error}</p>
 <Button
 onClick={() => window.location.reload()}
 className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all"
 >
 Retry
 </Button>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 relative overflow-hidden py-8 sm:py-12 px-4">
 <AnimatedBackground />
 <div className="max-w-7xl mx-auto relative z-10">
 {/* Header */}
 <div className="text-center mb-8 sm:mb-12 animate-fade-up">
 <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-600 shadow-2xl mb-4 sm:mb-6">
 <Briefcase className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
 </div>
 <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-3 sm:mb-4">
 My Applications
 </h1>
 <p className="text-muted-foreground dark:text-muted-foreground/80 text-base sm:text-lg max-w-2xl mx-auto px-4">
 Track the status of your job applications and stay updated on your career journey.
 </p>
 </div>

 {apps.length === 0 ? (
 <div className="text-center py-16 sm:py-20 animate-fade-up">
 <div className="/60 backdrop-blur-xl border border-white/50 dark:border-gray-700/50 rounded-3xl shadow-xl p-8 sm:p-12 max-w-md mx-auto">
 <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6 rounded-full bg-gradient-to-tr from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 flex items-center justify-center">
 <Briefcase className="w-10 h-10 sm:w-12 sm:h-12 text-indigo-600 dark:text-indigo-400" />
 </div>
 <p className="text-foreground/90 dark:text-gray-300 text-lg sm:text-xl font-semibold mb-2">
 No Applications Yet
 </p>
 <p className="text-muted-foreground dark:text-muted-foreground/80 text-sm sm:text-base mb-6">
 Start exploring and applying to jobs that match your skills and interests.
 </p>
 <Link to="/jobs">
 <Button className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105">
 Browse Jobs
 </Button>
 </Link>
 </div>
 </div>
 ) : (
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
 {apps.map((app, index) => {
 // Safely access job properties with defaults
 const job = app.job || {};
 const jobTitle = job.title || "Job Title Not Available";
 const companyName = job.recruiter?.companyName || 
 job.companyName || 
 "Company Not Available";
 const location = job.location || "Location Not Specified";
 const salaryMin = job.salaryMin || 0;
 const salaryMax = job.salaryMax || 0;
 const hasSalary = salaryMin > 0 || salaryMax > 0;
 const jobId = job.id || app.jobId || `app-${app.id}`;

 return (
 <div
 key={app.id || `app-${index}`}
 className="/60 backdrop-blur-xl border border-white/50 dark:border-gray-700/50 p-4 sm:p-6 rounded-2xl sm:rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 ease-in-out hover:border-indigo-300 dark:hover:border-indigo-600 transform hover:-translate-y-1 animate-fade-up"
 style={{ animationDelay: `${index * 50}ms` }}
 >
 <div className="flex justify-between items-start mb-4 gap-2">
 <div className="flex-1 min-w-0">
 <h3 className="text-base sm:text-lg font-semibold text-foreground dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors mb-2 line-clamp-2">
 {jobTitle}
 </h3>
 <div className="space-y-1.5">
 <p className="text-muted-foreground dark:text-muted-foreground/80 text-xs sm:text-sm flex items-center gap-1.5">
 <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
 <span className="truncate">{companyName}</span>
 </p>
 <p className="text-muted-foreground dark:text-muted-foreground/80 text-xs sm:text-sm flex items-center gap-1.5">
 <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
 <span className="truncate">{location}</span>
 </p>
 {hasSalary && (
 <p className="text-muted-foreground dark:text-muted-foreground/80 text-xs sm:text-sm flex items-center gap-1.5">
 <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
 <span className="truncate">
 {salaryMin.toLocaleString()} - {salaryMax.toLocaleString()} ETB
 </span>
 </p>
 )}
 <p className="text-muted-foreground dark:text-muted-foreground/80 text-xs sm:text-sm flex items-center gap-1.5">
 <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
 <span className="truncate">
 {new Date(app.appliedAt).toLocaleDateString()}
 </span>
 </p>
 </div>
 </div>
 <div className="flex-shrink-0">
 <StatusBadge status={app.status} />
 </div>
 </div>

 {app.coverLetter && (
 <div className="mb-4 p-3 bg-muted/30/50 rounded-xl border border-gray-200/50 dark:border-gray-700/50">
 <p className="text-xs sm:text-sm text-foreground/90 dark:text-gray-300 line-clamp-3">
 <strong className="text-indigo-600 dark:text-indigo-400">Cover Letter:</strong>{" "}
 {app.coverLetter}
 </p>
 </div>
 )}

 <div className="flex flex-col sm:flex-row gap-2">
 <Link to={`/jobs/${jobId}`} className="flex-1">
 <Button
 variant="outline"
 size="sm"
 className="w-full flex items-center justify-center gap-1.5 hover:bg-indigo-500/10 dark:hover:bg-indigo-900/20 hover:border-indigo-300 dark:hover:border-indigo-600 transition-all"
 >
 <Eye className="w-4 h-4" />
 <span className="text-sm">View Job</span>
 </Button>
 </Link>
 {app.status === "PENDING" && (
 <ConfirmDialog
 title="Withdraw Application?"
 description="This action cannot be undone. Your application will be permanently removed."
 onConfirm={() => handleWithdraw(app.id)}
 trigger={
 <Button 
 variant="destructive" 
 size="sm"
 disabled={actionLoading[app.id]}
 className="flex-1 sm:flex-none hover:scale-105 transition-transform"
 >
 {actionLoading[app.id] ? (
 <Loader2 className="w-4 h-4 animate-spin" />
 ) : (
 "Withdraw"
 )}
 </Button>
 }
 />
 )}
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 </div>
 );
};

export default MyApplications;